import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["input", "tagTemplate", "tagsList", "includeSelect", "excludeSelect", "tag", "submitButton", "countDisplay", "countText", "countRow"]

  static values = {
    name: String,
  }

  connect() {
    // Initialize tags from hidden selects
    Array.from(this.includeSelectTarget.options).forEach((option) => {
      if (option.selected) {
        this._appendTag(option.value, true)
      }
    })

    Array.from(this.excludeSelectTarget.options).forEach((option) => {
      if (option.selected) {
        this._appendTag(option.value, false)
      }
    })

    this._updateCount()

    // Watch for when the panel becomes visible
    this.setupVisibilityObserver()
  }

  setupVisibilityObserver() {
    // Find the parent panel element (the one with d-none that gets toggled)
    const panel = this.element.closest('[id*="job_titles-detail"]')
    if (!panel) return

    // Check if already visible
    if (!panel.classList.contains("d-none")) {
      this.focusInput()
    }

    // Watch for when d-none is removed (panel becomes visible)
    this.visibilityObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === "attributes" && mutation.attributeName === "class") {
          if (!panel.classList.contains("d-none")) {
            this.focusInput()
          }
        }
      })
    })

    this.visibilityObserver.observe(panel, {
      attributes: true,
      attributeFilter: ["class"]
    })
  }

  focusInput() {
    setTimeout(() => {
      if (this.hasInputTarget) {
        this.inputTarget.focus()
      }
    }, 100)
  }

  disconnect() {
    if (this.visibilityObserver) {
      this.visibilityObserver.disconnect()
    }
  }

  addTag(event) {
    // Only prevent default for Enter key, not blur
    if (event.type === "keydown" && event.key !== "Enter") {
      return
    }

    if (event.type === "keydown") {
      event.preventDefault()
    }

    const value = this.inputTarget.value.trim()

    if (value === "") {
      return
    }

    // Check if this job title already exists in either include or exclude lists
    const alreadyExists = Array.from(this.includeSelectTarget.options).some(opt => opt.value === value) ||
                         Array.from(this.excludeSelectTarget.options).some(opt => opt.value === value)

    if (alreadyExists) {
      // Job title already exists, clear input and return
      this.inputTarget.value = ""
      return
    }

    // Add to included by default
    this._appendTag(value, true)
    this._addTagToSelect(value, true)
    this.inputTarget.value = ""
    this._updateCount()
  }

  handleIncludeTag(event) {
    const tagElement = event.currentTarget.closest("[data-filters--job-titles-input-target='tag']")
    if (!tagElement) return

    const value = tagElement.dataset.value
    if (!value) return

    // Check if already in included
    const alreadyIncluded = Array.from(this.includeSelectTarget.options).some(opt => opt.value === value)
    if (alreadyIncluded) {
      return
    }

    // Move from exclude to include
    this._removeTagFromSelect(value, false)
    this._addTagToSelect(value, true)
    this._updateTagElement(tagElement, value, true)
  }

  handleExcludeTag(event) {
    const tagElement = event.currentTarget.closest("[data-filters--job-titles-input-target='tag']")
    if (!tagElement) return

    const value = tagElement.dataset.value
    if (!value) return

    // Check if already in excluded
    const alreadyExcluded = Array.from(this.excludeSelectTarget.options).some(opt => opt.value === value)
    if (alreadyExcluded) {
      return
    }

    // Move from include to exclude
    this._removeTagFromSelect(value, true)
    this._addTagToSelect(value, false)
    this._updateTagElement(tagElement, value, false)
  }

  removeTag(event) {
    const tagElement = event.currentTarget.closest("[data-filters--job-titles-input-target='tag']")
    if (!tagElement) return

    const value = tagElement.dataset.value
    if (!value) return

    // Remove from both selects
    this._removeTagFromSelect(value, true)
    this._removeTagFromSelect(value, false)
    tagElement.remove()
    this._updateCount()
  }

  apply() {
    // Dispatch event for job-filter controller
    const includedValues = Array.from(this.includeSelectTarget.selectedOptions).map(opt => opt.value)
    const excludedValues = Array.from(this.excludeSelectTarget.selectedOptions).map(opt => opt.value)

    const includedString = includedValues.join("|")
    const excludedString = excludedValues.join("|")

    this.dispatch("apply", {
      detail: { included: includedString, excluded: excludedString }
    })
  }

  clear() {
    // Remove all options from selects
    this.includeSelectTarget.innerHTML = ""
    this.excludeSelectTarget.innerHTML = ""

    // Remove all tag elements
    this.tagTargets.forEach(tag => tag.remove())
    this._updateCount()

    // Clear the input
    if (this.hasInputTarget) {
      this.inputTarget.value = ""
    }

    // Don't dispatch apply event - just clear locally and stay in view
  }

  _appendTag(value, included = true) {
    let tagHtml = this.tagTemplateTarget.innerHTML
    tagHtml = tagHtml.replace(/%LABEL%/g, this._escapeHtml(value))
    tagHtml = tagHtml.replace(/%VALUE%/g, this._escapeHtml(value))
    tagHtml = tagHtml.replace(/%INCLUDED%/g, included ? "true" : "false")

    let tagNode = document.createElement('div')
    tagNode.innerHTML = tagHtml

    this.tagsListTarget.appendChild(tagNode.firstElementChild)
  }

  _addTagToSelect(value, included) {
    let option = new Option(value, value, true, true)
    let selectElement = included ? this.includeSelectTarget : this.excludeSelectTarget
    selectElement.options.add(option)
  }

  _removeTagFromSelect(value, included) {
    const selectElement = included ? this.includeSelectTarget : this.excludeSelectTarget
    const index = Array.from(selectElement.options).findIndex((option) => {
      return option.value === value
    })
    if (index !== -1) {
      selectElement.options.remove(index)
    }
  }

  _updateTagElement(tagElement, value, included) {
    // Update the data attribute
    tagElement.setAttribute("data-signals--configuration--tag-included-value", included ? "true" : "false")

    // Update tag classes
    const tagLabel = tagElement.querySelector("[data-signals--configuration--tag-target='label']")
    if (tagLabel) {
      if (included) {
        tagLabel.classList.remove("tag--danger")
      } else {
        tagLabel.classList.add("tag--danger")
      }
    }

    // Update button states
    const includeButton = tagElement.querySelector("[data-signals--configuration--tag-target='includeButton']")
    const excludeButton = tagElement.querySelector("[data-signals--configuration--tag-target='excludeButton']")

    if (includeButton) {
      includeButton.classList.toggle("active", included)
    }
    if (excludeButton) {
      excludeButton.classList.toggle("active", !included)
    }
  }

  _updateCount() {
    if (!this.hasCountTextTarget) return

    const includedCount = Array.from(this.includeSelectTarget.selectedOptions).length
    const excludedCount = Array.from(this.excludeSelectTarget.selectedOptions).length
    const totalCount = includedCount + excludedCount

    // Show/hide the count row based on whether there are any items
    if (this.hasCountRowTarget) {
      if (totalCount === 0) {
        this.countRowTarget.classList.add("d-none")
      } else {
        this.countRowTarget.classList.remove("d-none")
      }
    }

    if (totalCount === 0) {
      this.countTextTarget.textContent = "0 items"
    } else {
      this.countTextTarget.textContent = `${totalCount} ${totalCount === 1 ? "item" : "items"}`
    }
  }

  _escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
